'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw, ShieldCheck, AlertTriangle, XCircle, Clock } from 'lucide-react';
import { reportsService, type ReportExport } from '@/lib/services/staffingService';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '@/components/ui/AppIcon';


interface CredRow {
  id: string;
  staffName: string;
  role: string;
  department: string;
  credentialType: string;
  credentialName: string;
  issuingBody: string;
  licenseNumber: string;
  issueDate: string;
  expiryDate: string;
  status: string;
  daysUntilExpiry: number;
  category: string;
}

type StatusFilter = 'all' | 'active' | 'expiring_soon' | 'expired' | 'pending_renewal';

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--success)',
  expiring_soon: 'var(--warning)',
  expired: 'var(--danger)',
  pending_renewal: 'var(--info)',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  pending_renewal: 'Pending Renewal',
};

interface Props {
  onExportLogged: (entry: ReportExport) => void;
}

export default function CredentialComplianceReport({ onExportLogged }: Props) {
  const [rows, setRows] = useState<CredRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsService.getCredentialComplianceSummary();
      setRows(data as CredRow[]);
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Failed to load credential data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter === 'all' ? rows : rows.filter((r) => r.status === statusFilter);

  // Status breakdown for pie
  const statusCounts = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(statusCounts).map(([name, value]) => ({
    name: STATUS_LABELS[name] || name,
    value,
    color: STATUS_COLORS[name] || '#888',
  }));

  const activeCount = statusCounts['active'] || 0;
  const expiringSoonCount = statusCounts['expiring_soon'] || 0;
  const expiredCount = statusCounts['expired'] || 0;
  const pendingCount = statusCounts['pending_renewal'] || 0;
  const complianceScore = rows.length > 0 ? Math.round((activeCount / rows.length) * 100) : 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportRows = statusFilter === 'all' ? rows : filtered;
      const csv = [
        ['Staff Name', 'Role', 'Department', 'Credential Type', 'Credential Name', 'Issuing Body', 'License #', 'Issue Date', 'Expiry Date', 'Status', 'Days Until Expiry', 'Category'].join(','),
        ...exportRows.map((r) => [
          `"${r.staffName}"`, r.role, `"${r.department}"`, `"${r.credentialType}"`,
          `"${r.credentialName}"`, `"${r.issuingBody}"`, r.licenseNumber,
          r.issueDate, r.expiryDate, r.status, r.daysUntilExpiry, r.category,
        ].join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      const fileName = `credential_compliance_${today}.csv`;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

      const logged = await reportsService.logExport({
        reportType: 'credential_compliance',
        title: `Credential Compliance Export — ${today}`,
        generatedBy: null,
        generatedByName: 'Current User',
        periodStart: today,
        periodEnd: today,
        filters: { statusFilter },
        rowCount: exportRows.length,
        fileName,
        status: 'completed',
        notes: statusFilter !== 'all' ? `Filtered by: ${statusFilter}` : '',
      });
      if (logged) onExportLogged(logged);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5 pt-5">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'active', 'expiring_soon', 'expired', 'pending_renewal'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-500 rounded-lg border transition-colors ${
                statusFilter === s
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-500 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button onClick={handleExport} disabled={exporting || rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50">
            <Download size={13} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Compliance Score', value: complianceScore + '%', icon: ShieldCheck, color: complianceScore >= 80 ? 'text-success' : complianceScore >= 60 ? 'text-warning' : 'text-danger' },
          { label: 'Active', value: activeCount.toString(), icon: ShieldCheck, color: 'text-success' },
          { label: 'Expiring Soon', value: expiringSoonCount.toString(), icon: Clock, color: 'text-warning' },
          { label: 'Expired', value: expiredCount.toString(), icon: XCircle, color: 'text-danger' },
          { label: 'Pending Renewal', value: pendingCount.toString(), icon: AlertTriangle, color: 'text-info' },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="card-elevated p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground font-500">{c.label}</p>
                <Icon size={15} className={c.color} />
              </div>
              <p className={`text-xl font-700 ${c.color}`}>{c.value}</p>
            </div>
          );
        })}
      </div>

      {error && <div className="bg-danger/10 border border-danger/20 rounded-lg px-4 py-3 text-sm text-danger">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Pie chart */}
        <div className="card-elevated p-5">
          <h3 className="text-sm font-600 text-foreground mb-4">Status Breakdown</h3>
          {loading ? (
            <div className="h-44 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : pieData.length === 0 ? (
            <div className="h-44 flex items-center justify-center text-sm text-muted-foreground">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Expiring soon list */}
        <div className="lg:col-span-2 card-elevated overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <h3 className="text-sm font-600 text-foreground">Expiring / Expired Credentials</h3>
          </div>
          <div className="overflow-y-auto max-h-52">
            {loading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-8 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    {['Staff', 'Credential', 'Expiry', 'Days Left', 'Status'].map((h) => (
                      <th key={h} className="text-left px-4 py-2 text-xs font-600 text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .filter((r) => r.status === 'expired' || r.status === 'expiring_soon')
                    .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
                    .slice(0, 10)
                    .map((r) => (
                      <tr key={r.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2 font-500 text-foreground">{r.staffName}</td>
                        <td className="px-4 py-2 text-muted-foreground text-xs">{r.credentialName}</td>
                        <td className="px-4 py-2 text-muted-foreground text-xs">{r.expiryDate}</td>
                        <td className="px-4 py-2">
                          <span className={`font-600 text-xs ${r.daysUntilExpiry < 0 ? 'text-danger' : 'text-warning'}`}>
                            {r.daysUntilExpiry < 0 ? `${Math.abs(r.daysUntilExpiry)}d ago` : `${r.daysUntilExpiry}d`}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-600 ${
                            r.status === 'expired' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'
                          }`}>
                            {STATUS_LABELS[r.status] || r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  {rows.filter((r) => r.status === 'expired' || r.status === 'expiring_soon').length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">All credentials are active ✓</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Full table */}
      <div className="card-elevated overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-600 text-foreground">Full Credential Inventory</h3>
          <span className="text-xs text-muted-foreground">{filtered.length} records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Staff Name', 'Role', 'Department', 'Credential', 'Issuing Body', 'Expiry Date', 'Status'].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-600 text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border/50">
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-3 bg-muted rounded animate-pulse w-20" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No credentials match the selected filter</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-2.5 font-500 text-foreground">{r.staffName}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.role}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.department}</td>
                    <td className="px-4 py-2.5 text-foreground">{r.credentialName}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{r.issuingBody}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.expiryDate}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-600 ${
                        r.status === 'active' ? 'bg-success/10 text-success' :
                        r.status === 'expiring_soon' ? 'bg-warning/10 text-warning' :
                        r.status === 'expired'? 'bg-danger/10 text-danger' : 'bg-info/10 text-info'
                      }`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
